import { cva, VariantProps } from "class-variance-authority";

export const buttonVariants = cva(
    // Base styles
    [
        // Layout & positioning ✅
        "inline-flex items-center justify-center box-border align-middle",

        // Typography ✅
        "font-bold text-s leading-s antialiased",

        // Sizing ✅
        "min-h-[max(2.5em,40px)] min-w-[max(2.5em,40px)] gap-1",

        // Borders & styling ✅
        "border border-solid border-transparent rounded-m cursor-default",

        // Hover styles
        "hover:cursor-pointer",

        // Focus styles
        "focus:outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus",

        // Disabled styles ✅
        "disabled:pointer-events-none disabled:opacity-25",

        // Default state
        "bg-transparent text-content-default border-transparent fill-current",
    ],
    {
        variants: {
            variant: {
                primary: [
                    "bg-interactive-default text-content-default-inverse border-transparent",
                    "hover:bg-interactive-hover",
                    "active:bg-interactive-pressed",
                ],
                secondary: [
                    "bg-transparent text-content-interactive-default border-content-interactive-default",
                    "hover:bg-hover hover:text-content-interactive-hover hover:border-content-interactive-hover",
                    "active:bg-transparent active:text-content-interactive-pressed active:border-content-interactive-pressed",
                ],
                tertiary: [
                    "bg-transparent text-content-interactive-default border-transparent",
                    "hover:bg-hover hover:text-interactive-hover",
                    "active:bg-pressed active:text-interactive-pressed",
                ],
            },
            size: {
                default: ["py-[7px)] px-[calc((var(--spacing)*5)-1px)]", "text-m leading-s"],
                s: [
                    "py-[calc((var(--spacing)*2)-1px)] px-[calc((var(--spacing)*4)-1px)]",
                    "text-s leading-s",
                    "min-h-[max(2em,32px)] min-w-[max(2em,32px)]",
                ],
            },
            destructive: {
                false: "",
                true: "",
            },
            inverse: {
                false: "",
                true: "",
            },
            iconOnly: {
                false: "",
                true: "py-[calc((var(--spacing)*1)-1px)] px-[calc((var(--spacing)*1)-1px)]",
            },
            hasIcon: {
                false: "",
                true: "",
            },
        },
        compoundVariants: [
            // Primary destructive variants
            {
                variant: "primary",
                destructive: true,
                inverse: false,
                class: ["bg-destructive-default", "hover:bg-destructive-hover", "active:bg-destructive-pressed"],
            },
            // Secondary destructive variants
            {
                variant: "secondary",
                destructive: true,
                inverse: false,
                class: [
                    "border-destructive-default text-content-destructive-default",
                    "hover:bg-hover hover:border-destructive-hover hover:text-content-destructive-hover",
                    "hover:bg-pressed active:border-destructive-pressed active:text-content-destructive-pressed",
                ],
            },
            // Tertiary destructive variants
            {
                variant: "tertiary",
                destructive: true,
                inverse: false,
                class: [
                    "bg-transparent border-transparent text-content-destructive-default",
                    "hover:bg-hover hover:text-content-destructive-hover",
                    "active:bg-pressed active:text-content-destructive-pressed",
                ],
            },

            // Primary inverse variants
            {
                variant: "primary",
                destructive: false,
                inverse: true,
                class: [
                    "bg-interactive-default-inverse text-content-interactive-default",
                    "hover:bg-interactive-hover-inverse hover:text-content-interactive-hover",
                    "active:bg-interactive-pressed-inverse hover:text-content-interactive-pressed",
                ],
            },
            // Secondary inverse variants
            {
                variant: "secondary",
                destructive: false,
                inverse: true,
                class: [
                    "bg-transparent border-interactive-default-inverse text-content-interactive-default-inverse",
                    "hover:bg-hover-inverse",
                    "active:bg-pressed-inverse",
                ],
            },
            // Tertiary inverse variants
            {
                variant: "tertiary",
                destructive: false,
                inverse: true,
                class: ["text-content-interactive-default-inverse", "hover:bg-hover-inverse", "active:bg-pressed-inverse"],
            },

            // Icon only variants
            {
                iconOnly: true,
                size: "default",
                class: ["py-[calc((var(--spacing)*2)-1px)] px-[calc((var(--spacing)*2)-1px)]"],
            },
            {
                iconOnly: true,
                size: "s",
                class: ["py-[calc((var(--spacing)*1)-1px)] px-[calc((var(--spacing)*1)-1px)]"],
            },
            // Icon only tertiary ✅
            {
                variant: "tertiary",
                iconOnly: true,
                class: [
                    "min-h-[max(2em,32px)] min-w-[max(2em,32px)]",
                    "py-[calc((var(--spacing)*1)-1px)] px-[calc((var(--spacing)*1)-1px)]",
                ],
            },
            // Icon only tertiary small ✅
            {
                variant: "tertiary",
                iconOnly: true,
                size: "s",
                class: [
                    "min-h-[max(1.5em,24px)] min-w-[max(1.5em,24px)]",
                    "py-[calc((var(--spacing)*1)-1px)] px-[calc((var(--spacing)*1)-1px)]",
                ],
            },

            // Has icon variants
            {
                hasIcon: true,
                size: "default",
                iconOnly: false,
                class: "pl-[calc((var(--spacing)*4)-1px)] pr-[calc((var(--spacing)*5)-1px)]",
            },
            {
                hasIcon: true,
                size: "s",
                iconOnly: false,
                class: "pl-[calc((var(--spacing)*3)-1px)] pr-[calc((var(--spacing)*4)-1px)]",
            },
        ],
        defaultVariants: {
            variant: "primary",
            size: "default",
            destructive: false,
            inverse: false,
            iconOnly: false,
            hasIcon: false,
        },
    }
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;
